import React, { useState, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useRecordsPage, useMessageChannelAdapter, useBreadcrumbRouteMetaEmitter } from 'hooks';
import { useFreshdeskContextState } from './freshdesk-context';
import { recordPageController } from 'controllers';

const initialContextValue = {
  loading: true,
  entity: null,
  associatedEntities: [],
  record: null,
  error: null,
  recordsListData: {},
  nativeDropdownChoicesByFieldId: new Map(),
};

export const RecordPageStateContext = React.createContext(initialContextValue);
const RecordPageUpdaterContext = React.createContext();

const RecordPageProvider = props => {
  const { state } = useLocation();
  const { fetchRecordAndEntityIfNull, fetchRecord: getRecord } = useRecordsPage();
  const { isFeatureEnabled } = useFreshdeskContextState();
  const { getNativeDropdownChoices } = useMessageChannelAdapter();
  const useDropdownLookup = isFeatureEnabled('useDropdownLookup');
  // The actual native-dropdown-choices API call is made by the host application over the message channel
  // (see useMessageChannelAdapter) - recordPageController only owns building filter params and mapping the
  // response, so it's handed the host-backed fetcher here rather than calling a REST service itself.
  recordPageController.nativeDropdownDataSource = { getNativeDropdownChoices };
  // Read via ref inside the load effect below instead of depending on useDropdownLookup directly - it
  // starts false and flips true once the Freshdesk context resolves, and depending on it directly would
  // re-run the whole fetchRecordAndEntityIfNull sequence a second time even when the flag's resolved value
  // never actually changes for the account.
  const useDropdownLookupRef = useRef(useDropdownLookup);
  useDropdownLookupRef.current = useDropdownLookup;
  // Tracks whether native dropdown choices have already been fetched for the record currently loaded -
  // reset on every new load, set true once the fetch below actually runs. If the flag was still false when
  // the main effect checked it, this stays false so the catch-up effect further down can retry once the
  // flag resolves true, instead of silently never fetching choices for that record view.
  const hasFetchedNativeDropdownChoicesRef = useRef(false);
  const { entity, associatedEntities, data: recordsListData } = state ?? { entity: null, associatedEntities: [] };
  const [recordPage, setRecordPage] = useState({ ...initialContextValue, entity, associatedEntities, recordsListData });
  const { displayId } = useParams();
  useBreadcrumbRouteMetaEmitter({
    entity: recordPage.entity,
    record: recordPage.record,
    displayId,
  });

  const fetchRecord = useCallback(async () => {
    setRecordPage(prev => {
      return {
        ...prev,
        loading: true,
      };
    });
    const recordData = await getRecord(recordPage.entity);
    setRecordPage(prev => {
      return {
        ...prev,
        ...recordData,
        loading: false,
      };
    });
  }, [recordPage.entity, getRecord]);

  useEffect(() => {
    hasFetchedNativeDropdownChoicesRef.current = false;
    (async () => {
      setRecordPage(prev => {
        return {
          ...prev,
          loading: true,
        };
      });
      const entityRecordData = await fetchRecordAndEntityIfNull(entity);
      if (Object.keys(entityRecordData).length > 0) {
        // Waits for the lookup-backed dropdown choices to resolve (accounts with useDropdownLookup only)
        // before marking the page loaded, so the record detail view renders resolved values straightaway
        // instead of rendering raw ids first and updating a moment later.
        let nativeDropdownChoicesByFieldId = initialContextValue.nativeDropdownChoicesByFieldId;
        const resolvedEntity = entityRecordData.entity ?? entity;
        if (useDropdownLookupRef.current && resolvedEntity?.fields) {
          const choicesResponse = await recordPageController.fetchNativeDropdownChoices(resolvedEntity.fields);
          if (choicesResponse?.success) {
            nativeDropdownChoicesByFieldId = choicesResponse.response;
          }
          hasFetchedNativeDropdownChoicesRef.current = true;
        }
        setRecordPage(prev => {
          return {
            ...prev,
            ...entityRecordData,
            nativeDropdownChoicesByFieldId,
            loading: false,
          };
        });
      }
    })();
    // useDropdownLookup is intentionally omitted - read via useDropdownLookupRef.current above instead, see
    // that ref's declaration comment.
  }, [entity, fetchRecordAndEntityIfNull]);

  // Catch-up for the race the effect above can't see: if useDropdownLookup was still false when the load
  // ran (the Freshdesk context hadn't resolved the flag yet), that effect never re-runs on its own since
  // useDropdownLookup isn't in its deps - without this, native dropdown choices would be skipped permanently
  // for that record view instead of just delayed. Fires once the flag/entity are both available and hasn't
  // already fetched for the current record.
  useEffect(() => {
    if (!useDropdownLookup || hasFetchedNativeDropdownChoicesRef.current || recordPage.loading || !recordPage.entity?.fields) {
      return;
    }
    hasFetchedNativeDropdownChoicesRef.current = true;
    (async () => {
      const choicesResponse = await recordPageController.fetchNativeDropdownChoices(recordPage.entity.fields);
      if (choicesResponse?.success) {
        setRecordPage(prev => ({ ...prev, nativeDropdownChoicesByFieldId: choicesResponse.response }));
      }
    })();
  }, [useDropdownLookup, recordPage.loading, recordPage.entity]);

  const updateFunctions = useMemo(() => {
    return {
      fetchRecord,
      setRecordPage,
    };
  }, [fetchRecord, setRecordPage]);

  return (
    <RecordPageStateContext.Provider value={recordPage}>
      <RecordPageUpdaterContext.Provider value={updateFunctions}>{props.children}</RecordPageUpdaterContext.Provider>
    </RecordPageStateContext.Provider>
  );
};

const useRecordPageState = () => {
  const recordPageState = useContext(RecordPageStateContext);
  if (!recordPageState) {
    throw new Error('useRecordPageState must be used within a RecordPageProvider');
  }
  return recordPageState;
};

const useRecordPageUpdater = () => {
  const { setRecordPage, fetchRecord } = useContext(RecordPageUpdaterContext);
  if (!setRecordPage || !fetchRecord) {
    throw new Error('useRecordPageUpdater must be used within a RecordPageProvider');
  }

  return { setRecordPage, fetchRecord };
};

export { RecordPageProvider, useRecordPageState, useRecordPageUpdater };
